import React from 'react';
import { useAuth } from '../context/AuthContext';

// Crops a face out of a photo purely with CSS (box = [ymin, xmin, ymax, xmax] in 0-1000 units).
export const FaceCrop = ({ src, box, size = 96, className = '', testId }) => {
  const { mediaUrl } = useAuth();
  const [y0, x0, y1, x1] = box;
  const pad = 60; // breathing room around the face
  const bx0 = Math.max(0, x0 - pad), by0 = Math.max(0, y0 - pad), bx1 = Math.min(1000, x1 + pad), by1 = Math.min(1000, y1 + pad);
  const w = Math.max(1, bx1 - bx0), h = Math.max(1, by1 - by0);
  const side = Math.max(w, h);
  const cx = (bx0 + bx1) / 2, cy = (by0 + by1) / 2;
  const left = Math.max(0, Math.min(1000 - side, cx - side / 2)), top = Math.max(0, Math.min(1000 - side, cy - side / 2));
  const scale = 1000 / side;
  return (
    <div
      className={`rounded-md overflow-hidden ${className}`}
      data-testid={testId}
      style={{
        width: size, height: size, backgroundImage: `url(${mediaUrl(src)})`,
        backgroundSize: `${scale * 100}% ${scale * 100}%`,
        backgroundPosition: `${(left / Math.max(1, 1000 - side)) * 100}% ${(top / Math.max(1, 1000 - side)) * 100}%`,
        backgroundRepeat: 'no-repeat', backgroundColor: '#ddd',
      }}
    />
  );
};

// Image with the detected face boxes drawn on top (object-fit: contain, letterboxing accounted for).
export const FaceOverlayImage = ({ src, faces, alt, className = '', onPick, activeId, testId }) => {
  const { mediaUrl } = useAuth();
  const ref = React.useRef(null);
  const [rect, setRect] = React.useState(null);
  const measure = React.useCallback(() => {
    const img = ref.current; if (!img || !img.naturalWidth) return;
    const cw = img.clientWidth, ch = img.clientHeight, r = img.naturalWidth / img.naturalHeight;
    const w = Math.min(cw, ch * r), h = Math.min(ch, cw / r);
    setRect({ left: (cw - w) / 2, top: (ch - h) / 2, w, h });
  }, []);
  React.useEffect(() => { window.addEventListener('resize', measure); return () => window.removeEventListener('resize', measure); }, [measure]);
  return (
    <div className={`relative ${className}`} data-testid={testId}>
      <img ref={ref} src={mediaUrl(src)} alt={alt || ''} onLoad={measure} className="w-full h-full" style={{ objectFit: 'contain' }} />
      {rect && (
        <div className="absolute" style={{ left: rect.left, top: rect.top, width: rect.w, height: rect.h }}>
          <FaceBoxes faces={faces} onPick={onPick} activeId={activeId} />
        </div>
      )}
    </div>
  );
};
export const FaceBoxes = ({ faces, onPick, activeId }) => (
  <>
    {(faces || []).map((f) => {
      const [y0, x0, y1, x1] = f.box;
      const label = f.name || f.suggested_name;
      return (
        <button
          type="button"
          key={f.id}
          onClick={() => onPick && onPick(f)}
          title={label ? `${f.name ? '' : 'Suggested: '}${label}` : 'Unnamed face — click to name'}
          data-testid={`face-box-${f.id}`}
          className="absolute"
          style={{
            left: `${x0 / 10}%`, top: `${y0 / 10}%`, width: `${(x1 - x0) / 10}%`, height: `${(y1 - y0) / 10}%`,
            border: `2px solid ${f.name ? '#1c6b2c' : f.id === activeId ? 'var(--gold)' : '#ffd97a'}`, borderRadius: 4,
            boxShadow: '0 0 0 1px rgba(0,0,0,0.5)', background: 'transparent', cursor: onPick ? 'pointer' : 'default',
          }}
        >
          {label && <span className="absolute left-0 -bottom-5 text-[10px] px-1 rounded whitespace-nowrap" style={{ background: f.name ? '#1c6b2c' : 'rgba(0,0,0,0.7)', color: 'white' }}>{label}</span>}
        </button>
      );
    })}
  </>
);
